import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Percent } from "lucide-react";

import { CurrencyText, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { Hr } from "@/components/common/other/Hr";
import { getRateWithCustomModel } from "@/state/loadRatesSlice";
import { useQuery } from "@/utils/query";
import { useIsBroker } from "@/auth/roles";
import { RateRangeBar } from "./RateRangeBar";
import { RateCardSkeleton } from "./RateCardSkeleton";
import { RateValueText } from "./RateValueText";
import { ConfidenceChip } from "./ConfidenceChip";
import { BuyRateCdfChart } from "./BuyRateCdfChart";
import { useQuantileCdf } from "./useRateDistributionFit";

export const UserBuyRate = () => {
    const dispatch = useDispatch();
    const { pickupZip, deliveryZip, trailerType, pickupDate } = useQuery();
    const { data: customModelData, loading, error } = useSelector((state) => state.loadRates.customModelRatePrediction);
    const { data: modelData } = useSelector((state) => state.loadRates.modelRatePrediction);
    const isBroker = useIsBroker();

    useEffect(() => {
        dispatch(getRateWithCustomModel({ trailerType, pickupZip, deliveryZip, pickupDate }));
    }, [trailerType, pickupZip, deliveryZip, pickupDate]);

    const buyRateBase = customModelData?.averageLoadPrice?.average ?? null;
    const perMile = customModelData?.averageRate?.average ?? null;
    const midLow = customModelData?.averageLoadPrice?.midLow ?? null;
    const midHigh = customModelData?.averageLoadPrice?.midHigh ?? null;
    // Brokers negotiate the other way round, so Start at / Leave at swap for them.
    const startAt = isBroker ? midLow : midHigh;
    const leaveAt = isBroker ? midHigh : midLow;
    // cmCertainty is already a score in [50, 100]
    const buyConfidence = customModelData?.cmCertainty != null ? Math.round(customModelData.cmCertainty) : null;

    const marketAverage = modelData?.averageLoadPrice?.average ?? null;
    const pctAboveMarket =
        marketAverage && buyRateBase ? Math.round(((buyRateBase - marketAverage) / marketAverage) * 100) : null;

    const cdf = useQuantileCdf(customModelData);
    const priceScale = buyRateBase != null && perMile ? buyRateBase / perMile : null;

    const [margin, setMargin] = useState("15");

    const handleMarginChange = (e) => {
        const raw = e.target.value;
        if (/^\d{0,3}(\.\d{0,2})?$/.test(raw)) {
            setMargin(raw);
        }
    };

    const netMargin = useMemo(() => {
        const m = Number(margin);
        if (buyRateBase == null || !Number.isFinite(m)) return 0;
        return Math.round((buyRateBase * m) / 100);
    }, [buyRateBase, margin]);

    if (loading || (!customModelData && !error)) {
        return (
            <RateCardSkeleton
                variant="buy"
                title="Your buy rate"
                infoText="The rate you should aim to pay the carrier for this load."
            />
        );
    }

    return (
        <div className="flex flex-col w-full grow gap-5">
          <div className="flex flex-col gap-7">
            <div className="flex flex-col gap-2">
                <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex flex-row items-center gap-2">
                        <TranslatedText className="text-base text-cm-p-text-primary font-medium leading-normal">
                            Your buy rate
                        </TranslatedText>
                        <InfoButton text="The rate you should aim to pay the carrier for this load." />
                    </div>
                    <ConfidenceChip value={buyConfidence} />
                </div>

                <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex flex-row items-center gap-2">
                        <RateValueText
                            num={buyRateBase}
                            round
                            emptyDash
                            className="text-cm-p-on-container-green font-medium text-2xl leading-[30px]"
                        />
                        <span className="text-cm-p-on-container-green/80 text-sm font-normal leading-normal">
                            {perMile ? perMile.toFixed(2) : " - "}/mi
                        </span>
                    </div>
                    {pctAboveMarket != null && pctAboveMarket !== 0 ? (
                        <span className="shrink-0 text-xs/[19px] text-cm-p-text-secondary whitespace-nowrap">
                            {Math.abs(pctAboveMarket)}% <TranslatedText>{pctAboveMarket > 0 ? "above" : "below"}</TranslatedText>{" "}
                            <TranslatedText>market rate</TranslatedText>
                        </span>
                    ) : null}
                </div>
            </div>

            {cdf ? (
                <BuyRateCdfChart cdf={cdf} buyRpm={perMile} priceScale={priceScale} />
            ) : (
                <RateRangeBar
                    leftLabel="Start at"
                    rightLabel="Leave at"
                    minValue={startAt}
                    maxValue={leaveAt}
                    markerValue={buyRateBase}
                    reversed
                    emptyDash
                    markerColorClass="bg-cm-p-on-container-green"
                />
            )}
          </div>

            <div className="flex flex-col grow gap-3">
                <Hr className="my-0" />

                <div className="flex flex-row items-center gap-2">
                    <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
                        <TranslatedText className="uppercase text-xs/[19px] text-cm-p-text-secondary font-medium">
                            Margin calculator
                        </TranslatedText>
                        <InfoButton text="Set your margin percentage to see the profit on top of the buy rate." />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-row gap-1">
                        <div className="flex h-10 min-w-0 flex-1 items-center gap-1 rounded-md border border-cm-p-outline bg-cm-p-surface-container px-3 py-2">
                            <input
                                type="text"
                                inputMode="decimal"
                                value={margin}
                                onChange={handleMarginChange}
                                className="w-full border-0 bg-transparent p-0 text-right text-sm font-medium text-cm-p-text-primary outline-none focus:ring-0"
                            />
                            <Percent className="h-4 w-4 shrink-0 text-cm-p-text-secondary" />
                        </div>
                        <div className="flex h-10 min-w-0 flex-1 items-center justify-end rounded-md bg-cm-p-container-green px-3 py-2">
                            <CurrencyText
                                num={netMargin}
                                round
                                canBeZero
                                className="text-base font-medium leading-normal text-cm-p-on-container-green"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
